import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { mergeSubscriptionParameters } from '@/api/auth'
import { usersApi } from '@/api/users'
import { subscriptionsApi, type SubscriptionPlan } from '@/api/subscriptions'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { STORAGE_KEYS } from '@/utils/constants'
import type { User } from '@/types'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Skeleton,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  TextField,
  Tab,
  Tabs,
} from '@mui/material'
import Link from '@mui/material/Link'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import StorageUsageIndicator from '@/components/storage/StorageUsageIndicator'
import { ArrowBack as ArrowBackIcon, Login as LoginIcon, Delete as DeleteIcon, Add as AddIcon, Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { ErrorPage } from '@/components/common/ErrorPage'
import { ContractStatusBadge } from '@/components/contracts/ContractStatusBadge'
import { teacherBalanceApi } from '@/api/teacherBalance'
import { formatMoney } from '@/components/billing/formatMoney'
import { getErrorMessage } from '@/utils/error'
import { getUserDisplayName } from '@/utils/userName'
import { AdminUserAuthEventsTab } from '@/components/admin/users/AdminUserAuthEventsTab'
import '@/styles/dashboard.css'

const TABS = ['profile', 'auth-events'] as const
type UserViewTab = (typeof TABS)[number]

export function AdminUserViewPage() {
  const { id } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab') ?? ''
  const tab: UserViewTab = (TABS as readonly string[]).includes(rawTab) ? (rawTab as UserViewTab) : 'profile'
  const token = useAuthStore((s) => s.token)
  const setAuthImpersonated = useAuthStore((s) => s.setAuthImpersonated)
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const [createSubOpen, setCreateSubOpen] = useState(false)
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [createSubPlanId, setCreateSubPlanId] = useState<number | ''>('')
  const [createSubStartsAt, setCreateSubStartsAt] = useState<Dayjs | null>(dayjs())
  const [createSubExpiresAt, setCreateSubExpiresAt] = useState<Dayjs | null>(null)
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustDirection, setAdjustDirection] = useState<'credit' | 'debit'>('credit')
  const [adjustDescription, setAdjustDescription] = useState('')
  const [adjustSubmitting, setAdjustSubmitting] = useState(false)
  const [adjustError, setAdjustError] = useState<string | null>(null)
  const [adjustedBalance, setAdjustedBalance] = useState<string | null>(null)
  const [createSubError, setCreateSubError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    usersApi
      .getById(Number(id))
      .then((data) => {
        if (!cancelled) setUser(data)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const error = err as { response?: { status?: number }; status?: number; message?: string }
          setErrorStatus(error?.response?.status ?? error?.status ?? null)
          setError(error?.message || t('common.error'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, t])

  const handleImpersonate = async () => {
    if (!id || !token) return
    localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, token)
    try {
      const res = await usersApi.impersonate(Number(id))
      // Токен пользователя ещё не в сторе: без него параметры подписки
      // пришли бы по токену админа
      const user = await mergeSubscriptionParameters(res.user, res.token)
      setAuthImpersonated(user, res.token)
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error?.message || t('common.error'))
    }
  }

  const handleDelete = async () => {
    if (!id || !window.confirm(t('users.deleteConfirm'))) return
    try {
      await usersApi.delete(Number(id))
      navigate(ROUTES.ADMIN_USERS, { replace: true })
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error?.message || t('common.error'))
    }
  }

  const loadPlans = async () => {
    try {
      const list = await subscriptionsApi.getPlans()
      setPlans(Array.isArray(list) ? list : [])
    } catch {
      setPlans([])
    }
  }

  const openCreateSubscription = () => {
    setCreateSubPlanId('')
    setCreateSubStartsAt(dayjs())
    setCreateSubExpiresAt(null)
    setCreateSubError(null)
    setCreateSubOpen(true)
    loadPlans()
  }

  const getCreateSubErrorMessage = (msg: string | null): string => {
    if (!msg) return ''
    if (msg.startsWith('subscriptions.')) {
      const key = `subscription.${msg.slice('subscriptions.'.length)}`
      const translated = t(key)
      if (translated !== key) return translated
    }
    return msg
  }

  const handleCreateSubscription = async () => {
    if (!user || !id || createSubPlanId === '' || !createSubStartsAt) return
    setCreateSubmitting(true)
    setCreateSubError(null)
    try {
      const startsAt = createSubStartsAt.format('YYYY-MM-DD')
      const expiresAt = createSubExpiresAt ? createSubExpiresAt.format('YYYY-MM-DD') : undefined
      await subscriptionsApi.createSubscriptionForUser(user.id, createSubPlanId, startsAt, expiresAt)
      setCreateSubOpen(false)
      const data = await usersApi.getById(Number(id))
      setUser(data)
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> }; status?: number }; message?: string }
      const raw = e?.response?.data?.errors?.expires_at?.[0] ?? e?.response?.data?.message ?? e?.message ?? t('common.error')
      setCreateSubError(raw)
    } finally {
      setCreateSubmitting(false)
    }
  }

  const getFullName = (u: User) =>
    getUserDisplayName(u, { prefer: 'full_name', withMiddleName: true })

  if (error && !user && !loading) {
    return <ErrorPage fullPage statusCode={errorStatus ?? 500} message={error} />
  }

  const userDisplayName = user ? getFullName(user) : null
  const isTeacher = user?.roles?.some((r) => r.slug === 'teacher') ?? false
  const isStudent = user?.roles?.some((r) => r.slug === 'student') ?? false

  return (
    <DashboardLayout>
      {/*
        Сетка с колонкой minmax(0, 1fr): прокручиваемые Tabs иначе задают
        минимальную ширину контента и дают горизонтальный скролл на телефоне
        (как на AdminSocialPage).
      */}
      <Box sx={{ p: { xs: 2, sm: 3 }, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', alignContent: 'start' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Link component={RouterLink} to={ROUTES.ADMIN_USERS} underline="hover">
            {t('admin.users.title')}
          </Link>
          <Typography color="text.primary">{userDisplayName ?? (loading ? '…' : t('admin.users.viewTitle'))}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton component={RouterLink} to={ROUTES.ADMIN_USERS} size="medium">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1, minWidth: 0 }}>
            {t('admin.users.viewTitle')}
          </Typography>
          <PageHelpPopover pageKey="admin-users" />
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, value: UserViewTab) => setSearchParams(value === 'profile' ? {} : { tab: value })}
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab value="profile" label={t('admin.users.tabs.profile')} />
          <Tab value="auth-events" label={t('admin.users.tabs.authEvents')} />
        </Tabs>

        {tab === 'auth-events' && id && <AdminUserAuthEventsTab userId={Number(id)} />}

        {tab !== 'profile' ? null : loading ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="text" width={200} height={40} />
              </Box>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={24} />
            </CardContent>
          </Card>
        ) : user ? (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('profile.personalInfo')}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">ID</Typography>
                    <Typography variant="body1">{user.id}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('admin.users.fullName')}</Typography>
                    <Typography variant="body1">{getFullName(user)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('common.email')}</Typography>
                    <Typography variant="body1">{user.email || '—'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('admin.users.roles')}</Typography>
                    <Typography variant="body1">{user.roles?.map((r) => r.slug).join(', ') || '—'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('admin.users.status')}</Typography>
                    <Typography variant="body1">{user.status || '—'}</Typography>
                  </Box>
                  {isTeacher && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">{t('admin.users.subscription')}</Typography>
                      <Typography variant="body1">
                        {user.active_subscription?.plan_name ?? t('admin.users.noSubscription')}
                      </Typography>
                    </Box>
                  )}
                  {isTeacher && user.storage_usage && (
                    <Box sx={{ mb: 2, maxWidth: 360 }}>
                      <StorageUsageIndicator
                        storage={user.storage_usage}
                        title={t('admin.users.storageCardTitle', 'Дисковое пространство')}
                        showLinks={false}
                        showWarning={false}
                      />
                    </Box>
                  )}
                  {isStudent && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">{t('users.contractLabel', { defaultValue: 'Договор' })}</Typography>
                      {user.contract ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <ContractStatusBadge status={user.contract.status} label={user.contract.status_label} />
                          {user.contract.group?.name && (
                            <Typography variant="body2" color="text.secondary">
                              {t('users.group', { defaultValue: 'Группа' })}: {user.contract.group.name}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {user.contract.starts_at}
                            {user.contract.expires_at ? ` — ${user.contract.expires_at}` : ''}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body1">{t('users.noContract', { defaultValue: 'Нет договора' })}</Typography>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  {isTeacher && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateSubscription} sx={{ height: 40 }}>
                      {t('admin.users.createSubscription')}
                    </Button>
                  )}
                  <Button variant="contained" startIcon={<LoginIcon />} onClick={handleImpersonate} sx={{ height: 40 }}>
                    {t('admin.users.impersonate')}
                  </Button>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to={ROUTES.ADMIN_USER_ACTIVITY(Number(id))}
                    sx={{ height: 40 }}
                  >
                    {t('activity.title')}
                  </Button>
                  <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete} sx={{ height: 40 }}>
                    {t('common.delete')}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {isTeacher && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('admin.balanceAdjust.title', 'Корректировка баланса')}
                  </Typography>
                  {adjustError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAdjustError(null)}>
                      {adjustError}
                    </Alert>
                  )}
                  {adjustedBalance !== null && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAdjustedBalance(null)}>
                      {t('admin.balanceAdjust.newBalance', 'Новый баланс')}: {formatMoney(adjustedBalance)}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField
                      label={t('admin.balanceAdjust.amount', 'Сумма, ₽')}
                      value={adjustAmount}
                      onChange={(e) => setAdjustAmount(e.target.value)}
                      type="number"
                      size="small"
                      inputProps={{ min: 0.01, step: '0.01' }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>{t('admin.balanceAdjust.direction', 'Направление')}</InputLabel>
                      <Select
                        value={adjustDirection}
                        label={t('admin.balanceAdjust.direction', 'Направление')}
                        onChange={(e) => setAdjustDirection(e.target.value as 'credit' | 'debit')}
                      >
                        <MenuItem value="credit">
                          {t('admin.balanceAdjust.credit', 'Пополнить')}
                        </MenuItem>
                        <MenuItem value="debit">
                          {t('admin.balanceAdjust.debit', 'Списать')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label={t('admin.balanceAdjust.description', 'Основание')}
                      value={adjustDescription}
                      onChange={(e) => setAdjustDescription(e.target.value)}
                      size="small"
                      sx={{ flex: 1, minWidth: 220 }}
                      inputProps={{ maxLength: 500 }}
                    />
                    <Button
                      variant="contained"
                      disabled={adjustSubmitting || !adjustAmount || !adjustDescription.trim()}
                      onClick={async () => {
                        if (!user) return
                        setAdjustSubmitting(true)
                        setAdjustError(null)
                        setAdjustedBalance(null)
                        try {
                          const result = await teacherBalanceApi.adminAdjustBalance(
                            user.id,
                            adjustAmount,
                            adjustDirection,
                            adjustDescription.trim(),
                          )
                          setAdjustedBalance(result.balance)
                          setAdjustAmount('')
                          setAdjustDescription('')
                        } catch (err) {
                          setAdjustError(getErrorMessage(err) || t('common.error'))
                        } finally {
                          setAdjustSubmitting(false)
                        }
                      }}
                    >
                      {t('admin.balanceAdjust.submit', 'Применить')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            <Dialog open={createSubOpen} onClose={() => { if (!createSubmitting) { setCreateSubError(null); setCreateSubOpen(false) } }} maxWidth="sm" fullWidth>
              <DialogTitle>{t('admin.users.createSubscriptionTitle')}</DialogTitle>
              <DialogContent>
                {createSubError && (
                  <Alert severity="error" onClose={() => setCreateSubError(null)} sx={{ mb: 2 }}>
                    {getCreateSubErrorMessage(createSubError)}
                  </Alert>
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                  <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
                    <InputLabel>{t('admin.subscriptions.plan')}</InputLabel>
                    <Select
                      value={createSubPlanId}
                      label={t('admin.subscriptions.plan')}
                      onChange={(e) => setCreateSubPlanId(String(e.target.value) === '' ? '' : Number(e.target.value))}
                    >
                      <MenuItem value="">{t('common.all')}</MenuItem>
                      {plans.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <DatePicker
                    label={t('admin.subscriptions.startsAt')}
                    value={createSubStartsAt}
                    onChange={(date) => setCreateSubStartsAt(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: { mb: 2 } } }}
                  />
                  <DatePicker
                    label={t('admin.users.subscriptionExpiresAtOptional')}
                    value={createSubExpiresAt}
                    onChange={(date) => setCreateSubExpiresAt(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setCreateSubOpen(false)} disabled={createSubmitting}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateSubscription}
                  disabled={createSubmitting || createSubPlanId === '' || !createSubStartsAt || (createSubExpiresAt != null && createSubStartsAt != null && createSubExpiresAt.isBefore(createSubStartsAt, 'day'))}
                >
                  {createSubmitting ? t('common.loading') : t('admin.users.createSubscription')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : null}
      </Box>
    </DashboardLayout>
  )
}
